/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef } from "react";
import axios from "axios";
import AccountList from "./components/AgentList";
import AccountSearch from "./components/AgentSearch";
import { FaPlus } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import AgentToolbar from "./components/AgentToolbar";
interface Filters {
  [key: string]: any;
}

const rowPerPage = 10;

const initialFilters: Filters = {
  suba_name: "",
};

interface PayloadType {
  [key: string]: any;
}

const Agent = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [lastKeys, setLastKeys] = useState<{ [key: number]: string }>({});
  const [currentLastKey, setCurrentLastKey] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);
  const navigate = useNavigate();
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  // NEW: control filter toggle
  const [showFilters, setShowFilters] = useState(false);

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create new AbortController for each batch of requests
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  useEffect(() => {
    const signal = getAbortSignal();
    const currentRequestId = ++requestIdRef.current;

    const fetchData = async () => {
      setIsLoading(true);
      await fetchAgents({
        page: 0,
        limit: rowPerPage,
        lastkey: "",
        ...filters,
      }, signal, currentRequestId);
      if (signal.aborted || requestIdRef.current !== currentRequestId) return;
      setIsLoading(false);
    };

    fetchData();
  }, []);

  const fetchAgents = async (payload: PayloadType = {}, signal?: AbortSignal, requestId?: number) => {
    try {
      const queryObject: PayloadType = {
        limit: payload?.limit,
        lastkey: payload?.lastkey,
        entity_type: "AGNT#",
      };

      if (payload?.suba_name !== "" && payload?.suba_name !== undefined) {
        queryObject.suba_name = payload?.suba_name;
      }

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/subaccount/get-all-subaccounts?${queryString}`,
        { signal }
      );

      if (signal?.aborted) return;
      if (requestId !== undefined && requestIdRef.current !== requestId) return;

      let newData = response.data.subaccounts?.payload;

      if (newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }

      let newLastKey = response.data.subaccounts?.lastkey;
      if (newLastKey) {
        setCurrentLastKey(newLastKey);
        let newPage = payload?.page;
        let newPageSize = payload?.limit;

        if (newData.length !== 0) {
          setRowCount((newPage + 1) * newPageSize + newPageSize);
        } else {
          setRowCount(newPage * newPageSize + newPageSize);
        }
        setLastKeys({ ...lastKeys, [newPage]: newLastKey });
      }
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      if (requestId !== undefined && requestIdRef.current !== requestId) return;
      setTableData([]);
      setPage(0);
      setPageSize(0);
      setRowCount(0);
      setLastKeys([]);
      setCurrentLastKey("");
      console.error("Error fetching data", error);
    }
  };

  const setPaginationModel = async (newValue: any) => {
    setIsLoading(true);

    let newFilters: any = {
      page: newValue.page,
      limit: newValue.pageSize,
      lastkey: currentLastKey,
      ...filters,
    };

    if (newValue.page < page) {
      newFilters = {
        ...newFilters,
        lastkey: newValue.page !== 0 ? lastKeys[newValue.page - 1] : "",
      };
    }

    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        lastkey: "",
        ...filters,
      };
      setPage(0);
      setPageSize(newValue.pageSize);
      setCurrentLastKey("");
      setLastKeys([]);
    } else {
      setPage(newValue.page);
    }

    await fetchAgents(newFilters);
    setIsLoading(false);
  };

  // const handleClickCreate = () => {
  //   navigate(`/${userRole}/account-management/create`);
  // };

  const handleSearchSubmit = async () => {
    setIsLoading(true);
    setCurrentLastKey("");
    setLastKeys({});
    setPage(0);
    await fetchAgents({ page: 0, limit: rowPerPage, lastkey: "", ...filters });
    setIsLoading(false);
  };

  const handleSearchClear = () => {
    handleResetTable();
  };

  const handleResetTable = async () => {
    setIsLoading(true);
    setFilters(initialFilters);
    setLastKeys([]);
    setCurrentLastKey("");
    setPage(0);
    await fetchAgents({ page: 0, limit: rowPerPage, lastkey: "", ...initialFilters });
    setIsLoading(false);
  };

  const handleClickCreate = () => {
    navigate(`/${userRole}/agents/create`);
  };

  const triggerReloadAgentList = async () => {
    setIsLoading(true);
    await fetchAgents({
      page: 0,
      limit: rowPerPage,
      lastkey: "",
      ...initialFilters,
    });
    setIsLoading(false);
  };

  return (
    <>
      <AgentToolbar
        isLoading={isLoading}
        onToggleFilters={() => setShowFilters((p) => !p)}
        onRefresh={triggerReloadAgentList}
        onNewAgent={handleClickCreate}
      />

      <AccountSearch
        showFilters={showFilters}
        isLoading={isLoading}
        filters={filters}
        setFilters={setFilters}
        handleSearchSubmit={handleSearchSubmit}
        handleSearchClear={handleSearchClear}
      />
      <AccountList
        isLoading={isLoading}
        page={page}
        pageSize={pageSize}
        rowCount={rowCount}
        tableData={isLoading ? [] : tableData}
        setPaginationModel={setPaginationModel}
        currentLastKey={currentLastKey}
      />
    </>
  );
};

export default Agent;
